import { Routes, Route, Navigate } from 'react-router-dom'
import ProtectedRoute from '@/src/ProtectedRoute'
import AuthCallback from '@/src/_pages/AuthCallback'
import SignIn from '@/src/_pages/SignIn'
import RedirectPage from '@/src/_pages/Redirect'
import ProtectedLayout from '@/src/ProtectedLayout'

// Lazy-load protected pages
import { Suspense } from 'react'
import { lazyWithRetry as lazy } from '@/src/lazy-with-retry'

const Dashboard = lazy(() => import('@/app/_protected/dashboard/page'))
const Credentials = lazy(() => import('@/app/_protected/credentials/page'))
const Employees = lazy(() => import('@/app/_protected/employees/page'))
const Clients = lazy(() => import('@/app/_protected/clients/page'))
const ClientDetail = lazy(() => import('@/app/_protected/clients/[id]/page'))
const ClientEdit = lazy(() => import('@/app/_protected/clients/[id]/edit/page'))
const ClientNew = lazy(() => import('@/app/_protected/clients/new/page'))
const ClientContacts = lazy(() => import('@/app/_protected/clients/[id]/contacts/new/page'))
const ClientContactEdit = lazy(() => import('@/app/_protected/clients/[id]/contacts/[contactId]/page'))
const Leaves = lazy(() => import('@/app/_protected/leaves/page'))
const LeavesAnalytics = lazy(() => import('@/app/_protected/leaves-analytics/page'))
const SSM = lazy(() => import('@/app/_protected/ssm/page'))
const SSMLiveMonitoring = lazy(() => import('@/app/_protected/ssm-live-monitoring/page'))
const ScreenIntegrityReport = lazy(() => import('@/app/_protected/screen-integrity-report/page'))
const RDPServers = lazy(() => import('@/app/_protected/rdp-servers/page'))
const Settings = lazy(() => import('@/app/_protected/settings/page'))
const CommitmentAgreement = lazy(() => import('@/app/_protected/commitment-agreement/page'))
const Inbox = lazy(() => import('@/app/_protected/inbox/page'))
const Trash = lazy(() => import('@/app/_protected/trash/page'))
const ZohoAudit = lazy(() => import('@/app/_protected/zoho-audit/page'))
const LateLoginReport = lazy(() => import('@/app/_protected/late-login-report/page'))
const EarlyLogoutReport = lazy(() => import('@/app/_protected/early-logout-report/page'))
const ActiveHoursReport = lazy(() => import('@/app/_protected/active-hours-report/page'))
const BreaktimeAnalysis = lazy(() => import('@/app/_protected/breaktime-analysis/page'))
const LeavePenaltyReport = lazy(() => import('@/app/_protected/leave-penalty-report/page'))
const AbsentReport = lazy(() => import('@/app/_protected/absent-report/page'))
const SaturdayWorking = lazy(() => import('@/app/_protected/saturday-working/page'))
const EmployeeComplianceReport = lazy(() => import('@/app/_protected/employee-compliance-report/page'))
const OnboardingTracker = lazy(() => import('@/app/_protected/onboarding-tracker/page'))
const ExitTracker = lazy(() => import('@/app/_protected/exit-tracker/page'))
const SopAssignments = lazy(() => import('@/app/_protected/sop-assignments/page'))
const TeamAssignment = lazy(() => import('@/app/_protected/team-assignment/page'))
const SopReviews = lazy(() => import('@/app/_protected/sop-reviews/page'))
const QcManualTrackers = lazy(() => import('@/app/_protected/qc-manual-trackers/page'))
const HourlyReport = lazy(() => import('@/app/_protected/hourly-report/page'))
const TaskPlanning = lazy(() => import('@/app/_protected/task-planning/page'))
const RolesAndResponsibilities = lazy(() => import('@/app/_protected/roles-and-responsibilities/page'))
const AimServerMonitoring = lazy(() => import('@/app/_protected/aim-server-monitoring/page'))
const GithubActionsUsage = lazy(() => import('@/app/_protected/github-actions-usage/page'))
const CunlHealth = lazy(() => import('@/app/_protected/cunl-health/page'))
const PublicSSM = lazy(() => import('@/app/_public/ssm/page'))

function PageLoader() {
  return (
    <div className="flex items-center justify-center py-24">
      <div className="text-muted-foreground text-sm">Loading...</div>
    </div>
  )
}

export default function App() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Public routes */}
        <Route path="/" element={<SignIn />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/public/ssm" element={<PublicSSM />} />

        {/* Protected routes */}
        <Route element={<ProtectedRoute />}>
          <Route element={<ProtectedLayout />}>
            <Route path="/redirect" element={<RedirectPage />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/credentials" element={<Credentials />} />
            <Route path="/employees" element={<Employees />} />
            <Route path="/clients" element={<Clients />} />
            <Route path="/clients/new" element={<ClientNew />} />
            <Route path="/clients/:id" element={<ClientDetail />} />
            <Route path="/clients/:id/edit" element={<ClientEdit />} />
            <Route path="/clients/:id/contacts/new" element={<ClientContacts />} />
            <Route path="/clients/:id/contacts/:contactId" element={<ClientContactEdit />} />
            <Route path="/leaves" element={<Leaves />} />
            <Route path="/leaves-analytics" element={<LeavesAnalytics />} />
            <Route path="/ssm" element={<SSM />} />
            <Route path="/ssm-live-monitoring" element={<SSMLiveMonitoring />} />
            <Route path="/screen-integrity-report" element={<ScreenIntegrityReport />} />
            <Route path="/rdp-servers" element={<RDPServers />} />
            <Route path="/settings" element={<Settings />} />
            {/* Retired 2026-10-27: the Performance Manager reports page and its tables were removed (CEO). Old links land on the dashboard. */}
            <Route path="/reports" element={<Navigate to="/dashboard" replace />} />
            <Route path="/commitment-agreement" element={<CommitmentAgreement />} />
            {/* Retired 2026-10-13: channels live on each client's Communication Channels tab. Old links land on Clients. */}
            <Route path="/comms-channels" element={<Navigate to="/clients" replace />} />
            <Route path="/hourly-report" element={<HourlyReport />} />
            <Route path="/task-planning" element={<TaskPlanning />} />
            <Route path="/roles-and-responsibilities" element={<RolesAndResponsibilities />} />
            <Route path="/roles-and-responsibilities/:role" element={<RolesAndResponsibilities />} />
            <Route path="/ops-manager-cunl" element={<Navigate to="/roles-and-responsibilities/ops-manager-cunl" replace />} />
            <Route path="/inbox" element={<Inbox />} />
            <Route path="/trash" element={<Trash />} />
            <Route path="/zoho-audit" element={<ZohoAudit />} />
            <Route path="/late-login-report" element={<LateLoginReport />} />
            <Route path="/early-logout-report" element={<EarlyLogoutReport />} />
            <Route path="/active-hours-report" element={<ActiveHoursReport />} />
            <Route path="/breaktime-analysis" element={<BreaktimeAnalysis />} />
            <Route path="/leave-penalty-report" element={<LeavePenaltyReport />} />
            <Route path="/absent-report" element={<AbsentReport />} />
            <Route path="/saturday-working" element={<SaturdayWorking />} />
            <Route path="/employee-compliance-report" element={<EmployeeComplianceReport />} />
            <Route path="/onboarding-tracker" element={<OnboardingTracker />} />
            <Route path="/exit-tracker" element={<ExitTracker />} />
            <Route path="/sop-assignments" element={<SopAssignments />} />
            <Route path="/team-assignment" element={<TeamAssignment />} />
            <Route path="/sop-reviews" element={<SopReviews />} />
            <Route path="/qc-manual-trackers" element={<QcManualTrackers />} />
            <Route path="/aim-server-monitoring" element={<AimServerMonitoring />} />
            <Route path="/github-actions-usage" element={<GithubActionsUsage />} />
            <Route path="/cunl-health" element={<CunlHealth />} />

            {/*
              Anything else a signed-in person asks for. Without this, an unknown path
              matched no route and React Router rendered NOTHING — the nav bar with a
              blank body, which reads as the app being broken rather than the page being
              gone. That is exactly what a bookmark to a deleted page produced:
              /upload-salary went blank the day the salary pages were removed.
              /redirect sends them to the first page they actually have access to.
            */}
            <Route path="*" element={<Navigate to="/redirect" replace />} />
          </Route>
        </Route>
      </Routes>
    </Suspense>
  )
}
